// src/components/scenes/SceneEditor.js
import React, { useState, useContext } from 'react';
import { Plus, Zap, Trash2 } from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { toast } from '../../lib/toast';
import { DialogHeader, DialogTitle } from '../ui/dialog';
import { ResponsiveSheet } from '../ui/responsive-sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import SceneDevicePicker from './SceneDevicePicker';
import DeviceActionEditor from './DeviceActionEditor';
import { describeSceneAction, buildActionFromLiveState, resolveActionDevice, normalizeAction } from '../../lib/sceneActions';
import { SCENE_CATEGORIES, iconForCategory } from '../../lib/sceneCategories';
import { getSceneBackground } from '../../lib/sceneBackgrounds';

/**
 * Guided scene builder — matches the mobile SceneBuilderSheet: a category-driven
 * icon + name, then a "When it runs" list of device actions added via a room-
 * grouped multi-select picker (each seeded from live state) and tweaked with the
 * capability-aware editor. Responsive: bottom sheet on mobile, centered modal on
 * desktop. One-shot save; legacy actions normalize to the canonical shape.
 */
const SceneEditor = ({ scene, onClose, onSave }) => {
  const { createScene, updateScene, devices } = useContext(AppContext);
  const isEdit = !!(scene && scene.id);

  const [name, setName] = useState(scene?.name || '');
  const [category, setCategory] = useState(scene?.category || 'General');
  const [actions, setActions] = useState(scene?.actions || []);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const resolvedId = (a) => resolveActionDevice(a, devices)?.id || a.device;
  const selectedDeviceIds = actions.filter((a) => a.type === 'device').map(resolvedId);
  const canSave = name.trim().length > 0 && actions.length > 0 && !saving;

  const knownCategory = SCENE_CATEGORIES.some((c) => c.toLowerCase() === (category || '').toLowerCase());
  const categoryOptions = knownCategory ? SCENE_CATEGORIES : [category, ...SCENE_CATEGORIES];
  const bg = getSceneBackground(category || 'General');
  const SceneIcon = iconForCategory(category);

  const handleDevicesPicked = (ids) => {
    setPickerOpen(false);
    const idSet = new Set(ids);
    setActions((prev) => {
      // Keep non-device actions, still-selected device actions, and any action
      // whose device can't be resolved (deleted device) — never silently drop.
      const kept = prev.filter((a) => {
        if (a.type !== 'device') return true;
        const resolved = resolveActionDevice(a, devices);
        return !resolved || idSet.has(resolved.id);
      });
      const existing = new Set(kept.filter((a) => a.type === 'device').map(resolvedId));
      const added = ids
        .filter((id) => !existing.has(id))
        .map((id) => devices.find((d) => d.id === id))
        .filter(Boolean)
        .map(buildActionFromLiveState);
      return [...kept, ...added];
    });
  };

  const saveAction = (action) => {
    setActions((prev) => {
      if (editingIndex != null) {
        const copy = [...prev];
        copy[editingIndex] = action;
        return copy;
      }
      return [...prev, action];
    });
    setEditingIndex(null);
  };

  const removeAction = (index) => setActions((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const draft = {
        ...(scene || {}),
        name: name.trim(),
        category,
        description: scene?.description ?? '',
        type: scene?.type || 'custom',
        actions: actions.map((a) => normalizeAction(a, devices)),
      };
      const saved = isEdit ? await updateScene(scene.id, draft) : await createScene(draft);
      onSave?.(saved);
      onClose();
    } catch (error) {
      console.error('Error saving scene:', error);
      toast.error('Failed to save scene');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ResponsiveSheet open onClose={() => { if (!saving) onClose?.(); }} size="lg">
          <DialogHeader className="shrink-0 px-10 pt-6 pb-4 border-b border-border">
            <DialogTitle className="text-lg font-bold text-center">{isEdit ? 'Edit Scene' : 'New Scene'}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-7">
            {/* Scene identity: category icon + name */}
            <section className="flex flex-col items-center gap-4">
              <div className={cn('h-16 w-16 rounded-2xl flex items-center justify-center bg-gradient-to-br border border-border/40', bg.gradient)}>
                <SceneIcon className={cn('h-7 w-7', bg.iconColor)} />
              </div>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Scene name"
                className="h-11 rounded-xl text-center"
              />
            </section>

            <section className="space-y-2">
              <Label className="text-xs font-medium uppercase text-muted-foreground">Category</Label>
              <div className="flex flex-wrap gap-2">
                {categoryOptions.map((cat) => {
                  const CatIcon = iconForCategory(cat);
                  const active = category === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategory(cat)}
                      className={cn(
                        'flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-muted/50 text-foreground border-border/60 hover:bg-muted',
                      )}
                    >
                      <CatIcon className="h-3.5 w-3.5" />
                      {cat}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Actions */}
            <section className="space-y-3">
              <Label className="text-xs font-medium uppercase text-muted-foreground">When it runs</Label>

              {actions.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Add devices and set how you want them when this scene runs.
                </p>
              )}

              <ul className="space-y-2">
                {actions.map((action, index) => (
                  <li key={index} className="flex items-center gap-2 rounded-xl border border-border/60 bg-card pl-3 pr-1.5 py-1.5">
                    <Zap className="h-4 w-4 text-muted-foreground shrink-0" />
                    <button type="button" onClick={() => setEditingIndex(index)} className="flex-1 min-w-0 text-left py-2 px-1">
                      <span className="text-sm block truncate">{describeSceneAction(action, devices)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeAction(index)}
                      className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                      aria-label="Remove action"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="flex items-center gap-2 py-2.5 px-1 text-sm font-medium text-primary hover:opacity-80 transition-opacity"
              >
                <Plus className="h-4 w-4" />
                Add devices
              </button>
            </section>
          </div>

          <div className="shrink-0 p-4 border-t border-border">
            <Button className="w-full h-11" onClick={handleSave} disabled={!canSave}>
              {saving ? 'Saving…' : 'Save Scene'}
            </Button>
          </div>
      </ResponsiveSheet>

      <SceneDevicePicker
        open={pickerOpen}
        devices={devices}
        selectedIds={selectedDeviceIds}
        onDone={handleDevicesPicked}
        onClose={() => setPickerOpen(false)}
      />

      <DeviceActionEditor
        open={editingIndex != null}
        action={editingIndex != null ? actions[editingIndex] : null}
        devices={devices}
        onSave={saveAction}
        onClose={() => setEditingIndex(null)}
      />
    </>
  );
};

export default SceneEditor;
